<template>
  <div class="ncrm-mailjet-workspace">
    <div class="d-flex flex-wrap align-center ga-3 mb-4">
      <h2 class="ncrm-mailjet-workspace__title">
        {{ $t("ncrm.integrations.mailjet.title") }}
      </h2>
      <span class="ncrm-mailjet-workspace__subtitle">
        {{ $t("ncrm.integrations.mailjet.subtitle") }}
      </span>
      <v-spacer />
      <v-btn color="primary" @click="openCreateDialog">
        <v-icon start>mdi-plus</v-icon>
        {{ $t("ncrm.integrations.mailjet.addAccount") }}
      </v-btn>
    </div>
    <v-data-table
      :headers="headers"
      :items="accounts"
      :loading="tableLoading"
      density="comfortable"
      class="elevation-0 ncrm-mailjet-accounts-table"
      item-key="id"
      :row-props="getRowProps"
      @click:row="onAccountRowClick"
    >
      <template #item.status="{ item }">
        <v-chip :color="item.status === 'active' ? 'success' : 'neutral'">
          {{ $t(`ncrm.integrations.mailjet.status.${item.status}`) }}
        </v-chip>
      </template>
      <template #item.last_test_status="{ item }">
        <v-chip :color="testStatusColor(item.last_test_status)">
          {{ $t(`ncrm.integrations.mailjet.lastTest.${item.last_test_status}`) }}
        </v-chip>
        <span v-if="item.last_tested_at" class="ml-2 text-caption text-medium-emphasis">
          {{ formatDate(item.last_tested_at) }}
        </span>
      </template>
      <template #item.mailjet_account_email="{ item }">
        <span v-if="item.mailjet_account_email">{{ item.mailjet_account_email }}</span>
        <span v-else class="text-medium-emphasis">—</span>
      </template>
      <template #item.actions="{ item }">
        <v-btn
          size="small"
          variant="tonal"
          color="primary"
          :loading="testingId === item.id"
          @click="handleTest(item)"
        >
          <v-icon start size="16">mdi-lan-connect</v-icon>
          {{ $t("ncrm.integrations.mailjet.actions.test") }}
        </v-btn>
        <v-btn size="small" variant="text" class="ml-1" @click="openEditDialog(item)">
          <v-icon size="18">mdi-pencil</v-icon>
        </v-btn>
        <v-btn size="small" variant="text" class="ml-1" @click="openSenderDialog(item)">
          <v-icon size="18">mdi-email-edit-outline</v-icon>
        </v-btn>
        <v-btn size="small" variant="text" color="error" class="ml-1" @click="openDeleteDialog(item)">
          <v-icon size="18">mdi-delete-outline</v-icon>
        </v-btn>
      </template>
      <template #no-data>
        <v-alert type="info" class="ma-4">
          {{ $t("ncrm.integrations.mailjet.empty") }}
        </v-alert>
      </template>
    </v-data-table>
    <NcrmMailjetDataPanel
      v-if="selectedAccount"
      :account="selectedAccount"
      @notify="onPanelNotify"
      @sync-completed="fetchAccounts"
      @account-updated="onAccountUpdated"
    />
    <!-- Create / Edit dialog -->
    <v-dialog v-model="formDialog.show" max-width="560" persistent>
      <v-card>
        <v-card-title>
          {{
            formDialog.mode === "create"
              ? $t("ncrm.integrations.mailjet.dialog.create.title")
              : $t("ncrm.integrations.mailjet.dialog.edit.title")
          }}
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <v-text-field
            v-model="formDialog.data.name"
            :label="$t('ncrm.integrations.mailjet.dialog.fields.name')"
            :rules="[required]"
            required
          />
          <v-textarea
            v-model="formDialog.data.description"
            :label="$t('ncrm.integrations.mailjet.dialog.fields.description')"
            rows="2"
            auto-grow
          />
          <v-select
            v-if="formDialog.mode === 'edit'"
            v-model="formDialog.data.status"
            :items="statusOptions"
            :label="$t('ncrm.integrations.mailjet.dialog.fields.status')"
          />
          <v-checkbox
            v-if="formDialog.mode === 'edit'"
            v-model="formDialog.editKeys"
            :label="$t('ncrm.integrations.mailjet.dialog.editKeys')"
            density="comfortable"
            hide-details
            class="mb-2"
          />
          <template v-if="formDialog.mode === 'create' || formDialog.editKeys">
            <v-text-field
              v-model="formDialog.data.apiKey"
              :label="$t('ncrm.integrations.mailjet.dialog.fields.apiKey')"
              :rules="[required]"
              required
              autocomplete="off"
            />
            <v-text-field
              v-model="formDialog.data.apiSecret"
              :label="$t('ncrm.integrations.mailjet.dialog.fields.apiSecret')"
              type="password"
              :rules="[required]"
              required
              autocomplete="off"
            />
          </template>
          <v-alert v-if="formDialog.error" type="error" class="mt-2">
            {{ formDialog.error }}
          </v-alert>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="formDialog.saving" @click="closeFormDialog">
            {{ $t("ncrm.integrations.mailjet.dialog.actions.cancel") }}
          </v-btn>
          <v-btn color="primary" :loading="formDialog.saving" @click="submitForm">
            {{ $t("ncrm.integrations.mailjet.dialog.actions.save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <NcrmMailjetSenderDialog
      v-model="senderDialog.show"
      :account="senderDialog.account"
      @notify="onPanelNotify"
      @saved="handleSenderSaved"
    />
    <NcrmMailjetDeleteDialog
      v-model="deleteDialog.show"
      :account="deleteDialog.account"
      @notify="onPanelNotify"
      @deleted="handleAccountDeleted"
    />
    <v-snackbar v-model="snackbar.show" :timeout="3500" :color="snackbar.color">
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>
<script>
import axios from "axios";
import NcrmMailjetDataPanel from "@/components/ncrm/integrations/mailjet/NcrmMailjetDataPanel.vue";
import NcrmMailjetDeleteDialog from "@/components/ncrm/integrations/mailjet/NcrmMailjetDeleteDialog.vue";
import NcrmMailjetSenderDialog from "@/components/ncrm/integrations/mailjet/NcrmMailjetSenderDialog.vue";
import { formatDisplayDateTime } from "@/Utile/displayFormat.mjs";
const buildEmptyForm = () => ({
  id: null,
  name: "",
  description: "",
  apiKey: "",
  apiSecret: "",
  status: "active",
  defaultSenderEmail: "",
  defaultSenderName: "",
});
export default {
  name: "NcrmMailjetWorkspace",
  components: {
    NcrmMailjetDataPanel,
    NcrmMailjetDeleteDialog,
    NcrmMailjetSenderDialog,
  },
  data() {
    return {
      accounts: [],
      tableLoading: false,
      testingId: null,
      selectedAccountId: null,
      formDialog: {
        show: false,
        mode: "create",
        editKeys: false,
        saving: false,
        error: "",
        data: buildEmptyForm(),
      },
      deleteDialog: {
        show: false,
        account: null,
      },
      senderDialog: {
        show: false,
        account: null,
      },
      snackbar: {
        show: false,
        message: "",
        color: "info",
      },
    };
  },
  computed: {
    headers() {
      return [
        { title: this.$t("ncrm.integrations.mailjet.headers.name"), key: "name" },
        { title: this.$t("ncrm.integrations.mailjet.headers.description"), key: "description" },
        { title: this.$t("ncrm.integrations.mailjet.headers.status"), key: "status" },
        { title: this.$t("ncrm.integrations.mailjet.headers.lastTest"), key: "last_test_status" },
        { title: this.$t("ncrm.integrations.mailjet.headers.testEmail"), key: "mailjet_account_email" },
        { title: this.$t("ncrm.integrations.mailjet.headers.actions"), key: "actions", sortable: false, align: "end" },
      ];
    },
    statusOptions() {
      return [
        { title: this.$t("ncrm.integrations.mailjet.status.active"), value: "active" },
        { title: this.$t("ncrm.integrations.mailjet.status.disabled"), value: "disabled" },
      ];
    },
    selectedAccount() {
      if (!this.selectedAccountId) return null;
      return this.accounts.find((acc) => acc.id === this.selectedAccountId) || null;
    },
  },
  mounted() {
    this.fetchAccounts();
  },
  methods: {
    required(value) {
      return !!String(value || "").trim() || this.$t("ncrm.integrations.mailjet.dialog.requiredField");
    },
    formatDate(value) {
      if (!value) return "";
      try {
        return formatDisplayDateTime(value);
      } catch (_) {
        return String(value);
      }
    },
    testStatusColor(status) {
      if (status === "ok") return "success";
      if (status === "error") return "error";
      return "neutral";
    },
    showSnackbar(message, color = "info") {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
    extractError(error, fallbackKey) {
      return (
        error?.response?.data?.error ||
        error?.message ||
        this.$t(fallbackKey)
      );
    },
    async fetchAccounts() {
      this.tableLoading = true;
      try {
        const { data } = await axios.get("/ncrm/integrations/mailjet/accounts");
        this.accounts = Array.isArray(data?.accounts) ? data.accounts : [];
        if (this.accounts.length === 1) {
          this.selectedAccountId = this.accounts[0].id;
        } else if (
          this.selectedAccountId &&
          !this.accounts.some((acc) => acc.id === this.selectedAccountId)
        ) {
          this.selectedAccountId = null;
        }
      } catch (error) {
        this.showSnackbar(
          this.extractError(error, "ncrm.integrations.mailjet.snackbar.loadError"),
          "error"
        );
      } finally {
        this.tableLoading = false;
      }
    },
    getRowProps({ item }) {
      if (!item) return {};
      const isSelected = this.selectedAccountId === item.id;
      return {
        class: isSelected ? "ncrm-mailjet-row--selected" : "",
        style: { cursor: "pointer" },
      };
    },
    onAccountRowClick(_event, row) {
      const item = row?.item || row;
      if (!item || !item.id) return;
      this.selectedAccountId = item.id === this.selectedAccountId ? null : item.id;
    },
    onPanelNotify(payload) {
      if (!payload) return;
      this.showSnackbar(payload.message || "", payload.color || "info");
    },
    onAccountUpdated(updated) {
      if (!updated || !updated.id) return;
      const idx = this.accounts.findIndex((a) => a.id === updated.id);
      if (idx >= 0) {
        this.accounts.splice(idx, 1, { ...this.accounts[idx], ...updated });
      }
    },
    openCreateDialog() {
      this.formDialog = {
        show: true,
        mode: "create",
        editKeys: false,
        saving: false,
        error: "",
        data: buildEmptyForm(),
      };
    },
    openEditDialog(account) {
      this.formDialog = {
        show: true,
        mode: "edit",
        editKeys: false,
        saving: false,
        error: "",
        data: {
          id: account.id,
          name: account.name || "",
          description: account.description || "",
          apiKey: "",
          apiSecret: "",
          status: account.status || "active",
          defaultSenderEmail: account.default_sender_email || "",
          defaultSenderName: account.default_sender_name || "",
        },
      };
    },
    closeFormDialog() {
      this.formDialog.show = false;
    },
    openSenderDialog(account) {
      this.senderDialog = {
        show: true,
        account,
      };
    },
    async handleSenderSaved() {
      await this.fetchAccounts();
    },
    async submitForm() {
      this.formDialog.error = "";
      const { mode, editKeys, data } = this.formDialog;
      if (!data.name.trim()) {
        this.formDialog.error = this.$t("ncrm.integrations.mailjet.dialog.requiredField");
        return;
      }
      if ((mode === "create" || editKeys) && (!data.apiKey.trim() || !data.apiSecret.trim())) {
        this.formDialog.error = this.$t("ncrm.integrations.mailjet.dialog.requiredField");
        return;
      }
      this.formDialog.saving = true;
      try {
        if (mode === "create") {
          const payload = {
            name: data.name.trim(),
            description: data.description?.trim() || null,
            apiKey: data.apiKey.trim(), apiSecret: data.apiSecret.trim(),
            defaultSenderEmail: data.defaultSenderEmail?.trim() || null, defaultSenderName: data.defaultSenderName?.trim() || null,
          };
          await axios.post("/ncrm/integrations/mailjet/accounts", payload);
          this.showSnackbar(this.$t("ncrm.integrations.mailjet.snackbar.created"), "success");
        } else {
          const payload = {
            name: data.name.trim(),
            description: data.description?.trim() || null,
            status: data.status,
            defaultSenderEmail: data.defaultSenderEmail?.trim() || null,
            defaultSenderName: data.defaultSenderName?.trim() || null,
          };
          if (editKeys) {
            payload.apiKey = data.apiKey.trim();
            payload.apiSecret = data.apiSecret.trim();
          }
          await axios.patch(`/ncrm/integrations/mailjet/accounts/${data.id}`, payload);
          this.showSnackbar(this.$t("ncrm.integrations.mailjet.snackbar.updated"), "success");
        }
        this.formDialog.show = false;
        await this.fetchAccounts();
      } catch (error) {
        this.formDialog.error = this.extractError(
          error,
          "ncrm.integrations.mailjet.snackbar.saveError"
        );
      } finally {
        this.formDialog.saving = false;
      }
    },
    openDeleteDialog(account) {
      this.deleteDialog = {
        show: true,
        account,
      };
    },
    async handleAccountDeleted() {
      await this.fetchAccounts();
    },
    async handleTest(account) {
      this.testingId = account.id;
      try {
        const { data } = await axios.post(
          `/ncrm/integrations/mailjet/accounts/${account.id}/test`,
          {},
          { validateStatus: () => true }
        );
        if (data?.ok) {
          this.showSnackbar(
            this.$t("ncrm.integrations.mailjet.snackbar.testOk", {
              email: data.mailjetAccountEmail || account.name,
            }),
            "success"
          );
        } else {
          this.showSnackbar(
            data?.error || this.$t("ncrm.integrations.mailjet.snackbar.testError"),
            "error"
          );
        }
        await this.fetchAccounts();
      } catch (error) {
        this.showSnackbar(
          this.extractError(error, "ncrm.integrations.mailjet.snackbar.testError"),
          "error"
        );
      } finally {
        this.testingId = null;
      }
    },
  },
};
</script>
<style scoped>
.ncrm-mailjet-workspace__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--app-text-strong);
}
.ncrm-mailjet-workspace__subtitle {
  font-size: 0.82rem;
  color: var(--app-text-default);
  opacity: 0.7;
  padding-left: 8px;
  border-left: 1px solid rgba(var(--v-theme-on-surface), 0.18);
}
.ncrm-mailjet-accounts-table :deep(.ncrm-mailjet-row--selected) {
  background-color: rgba(var(--v-theme-primary), 0.08);
}
</style>
